/**
 * Role Context Helper Functions
 * 
 * Role context and utility functions providing:
 * - RoleContext creation
 * - useRole hook
 * - Role definitions (admin, manager, client, operator, etc.)
 * - Permission definitions (WMS, TMS, OMS, etc.)
 * - Role-based access control utilities
 * 
 * @module contexts/RoleContext.helpers
 */

import React, { createContext, useContext } from 'react';

export const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  CLIENT: 'client',
  OPERATOR: 'operator',
  TEAM_LEADER: 'team_leader',
  ANALYST: 'analyst',
  SELF_SERVICE_FBA: 'self_service_fba',
  WORKSTATION_PREPARATION: 'workstation_preparation',
  WORKSTATION_RECEIVING: 'workstation_receiving',
  WORKSTATION_RECEIVING_SECURE: 'workstation_receiving_secure',
  WORKSTATION_RETURN: 'workstation_return'
};

export const PERMISSIONS = {
  WMS_VIEW: 'wms_view',
  WMS_EDIT: 'wms_edit',
  WMS_ADMIN: 'wms_admin',
  TMS_VIEW: 'tms_view',
  TMS_EDIT: 'tms_edit',
  TMS_ADMIN: 'tms_admin',
  OMS_VIEW: 'oms_view',
  OMS_EDIT: 'oms_edit',
  OMS_ADMIN: 'oms_admin',
  DASHBOARD_EXECUTIVE: 'dashboard_executive',
  REPORTS_ADVANCED: 'reports_advanced',
  TEAM_MANAGEMENT: 'team_management',
  ANALYTICS_FULL: 'analytics_full',
  ALERTS_MANAGEMENT: 'alerts_management',
  TEAM_SUPERVISION: 'team_supervision',
  TASK_ASSIGNMENT: 'task_assignment',
  PERFORMANCE_MONITORING: 'performance_monitoring',
  SHIFT_PLANNING: 'shift_planning',
  PICKING_PATH: 'picking_path',
  RECEIVING_PLANNING: 'receiving_planning',
  LOCATION_MANAGEMENT: 'location_management',
  RETURNS_PROCESSING: 'returns_processing',
  WORKSTATION_ACCESS: 'workstation_access',
  WORKSTATION_PREPARATION: 'workstation_preparation',
  WORKSTATION_RECEIVING: 'workstation_receiving',
  WORKSTATION_RECEIVING_SECURE: 'workstation_receiving_secure',
  WORKSTATION_RETURN: 'workstation_return',
  WORKSTATION_MONITORING: 'workstation_monitoring',
  WORKSTATION_CONFIG: 'workstation_config',
  WORKSTATION_DEBUG: 'workstation_debug',
  SYSTEM_CONFIG: 'system_config',
  USER_MANAGEMENT: 'user_management',
  BILLING_VIEW: 'billing_view',
  BILLING_EDIT: 'billing_edit'
};

export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [...Object.values(PERMISSIONS)],
  [ROLES.MANAGER]: [
    PERMISSIONS.WMS_VIEW,
    PERMISSIONS.TMS_VIEW,
    PERMISSIONS.OMS_VIEW,
    PERMISSIONS.WMS_EDIT,
    PERMISSIONS.TMS_EDIT,
    PERMISSIONS.OMS_EDIT,
    PERMISSIONS.DASHBOARD_EXECUTIVE,
    PERMISSIONS.REPORTS_ADVANCED,
    PERMISSIONS.TEAM_MANAGEMENT,
    PERMISSIONS.ANALYTICS_FULL,
    PERMISSIONS.ALERTS_MANAGEMENT,
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.WORKSTATION_ACCESS,
    PERMISSIONS.WORKSTATION_PREPARATION,
    PERMISSIONS.WORKSTATION_RECEIVING,
    PERMISSIONS.WORKSTATION_RECEIVING_SECURE,
    PERMISSIONS.WORKSTATION_RETURN,
    PERMISSIONS.WORKSTATION_MONITORING,
    PERMISSIONS.WORKSTATION_CONFIG
  ],
  [ROLES.CLIENT]: [
    PERMISSIONS.WMS_VIEW,
    PERMISSIONS.TMS_VIEW,
    PERMISSIONS.OMS_VIEW,
    PERMISSIONS.BILLING_VIEW
  ],
  [ROLES.OPERATOR]: [
    PERMISSIONS.WMS_VIEW,
    PERMISSIONS.WMS_EDIT,
    PERMISSIONS.TMS_VIEW,
    PERMISSIONS.OMS_VIEW,
    PERMISSIONS.PICKING_PATH,
    PERMISSIONS.RECEIVING_PLANNING,
    PERMISSIONS.LOCATION_MANAGEMENT,
    PERMISSIONS.RETURNS_PROCESSING,
    PERMISSIONS.WORKSTATION_ACCESS,
    PERMISSIONS.WORKSTATION_PREPARATION,
    PERMISSIONS.WORKSTATION_RECEIVING,
    PERMISSIONS.WORKSTATION_RECEIVING_SECURE,
    PERMISSIONS.WORKSTATION_RETURN
  ],
  [ROLES.TEAM_LEADER]: [
    PERMISSIONS.WMS_VIEW,
    PERMISSIONS.WMS_EDIT,
    PERMISSIONS.TMS_VIEW,
    PERMISSIONS.OMS_VIEW,
    PERMISSIONS.TEAM_SUPERVISION,
    PERMISSIONS.TASK_ASSIGNMENT,
    PERMISSIONS.PERFORMANCE_MONITORING,
    PERMISSIONS.SHIFT_PLANNING,
    PERMISSIONS.PICKING_PATH,
    PERMISSIONS.RECEIVING_PLANNING,
    PERMISSIONS.LOCATION_MANAGEMENT,
    PERMISSIONS.RETURNS_PROCESSING,
    PERMISSIONS.WORKSTATION_ACCESS,
    PERMISSIONS.WORKSTATION_PREPARATION,
    PERMISSIONS.WORKSTATION_RECEIVING,
    PERMISSIONS.WORKSTATION_RECEIVING_SECURE,
    PERMISSIONS.WORKSTATION_RETURN
  ],
  [ROLES.ANALYST]: [
    PERMISSIONS.WMS_VIEW,
    PERMISSIONS.TMS_VIEW,
    PERMISSIONS.OMS_VIEW,
    PERMISSIONS.ANALYTICS_FULL,
    PERMISSIONS.REPORTS_ADVANCED
  ],
  [ROLES.SELF_SERVICE_FBA]: [
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.BILLING_EDIT
  ]
};

export const getRoleDisplayName = (role) => {
  const names = {
    [ROLES.ADMIN]: 'Administrator',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.CLIENT]: 'Client',
    [ROLES.OPERATOR]: 'Operator',
    [ROLES.TEAM_LEADER]: 'Team Leader',
    [ROLES.ANALYST]: 'Analyst',
    [ROLES.SELF_SERVICE_FBA]: 'Self-Service FBA Client',
    [ROLES.WORKSTATION_PREPARATION]: 'Workstation Preparation',
    [ROLES.WORKSTATION_RECEIVING]: 'Workstation Receiving',
    [ROLES.WORKSTATION_RECEIVING_SECURE]: 'Workstation Receiving Secure',
    [ROLES.WORKSTATION_RETURN]: 'Workstation Back'
  };
  return names[role] || 'User';
};

export const getRoleTheme = (role) => {
  const themes = {
    [ROLES.ADMIN]: 'admin',
    [ROLES.MANAGER]: 'executive',
    [ROLES.CLIENT]: 'client',
    [ROLES.OPERATOR]: 'operator',
    [ROLES.TEAM_LEADER]: 'team-leader',
    [ROLES.ANALYST]: 'analyst',
    [ROLES.SELF_SERVICE_FBA]: 'self-service',
    [ROLES.WORKSTATION_PREPARATION]: 'workstation-preparation',
    [ROLES.WORKSTATION_RECEIVING]: 'workstation-receiving',
    [ROLES.WORKSTATION_RECEIVING_SECURE]: 'workstation-secure',
    [ROLES.WORKSTATION_RETURN]: 'workstation-return'
  };
  return themes[role] || 'default';
};

export const getRoleDefaultRoute = (role) => {
  const routes = {
    [ROLES.ADMIN]: '/admin/dashboard',
    [ROLES.MANAGER]: '/manager/dashboard',
    [ROLES.CLIENT]: '/dashboard',
    [ROLES.OPERATOR]: '/operator/dashboard',
    [ROLES.TEAM_LEADER]: '/team-leader/dashboard',
    [ROLES.ANALYST]: '/analyst/dashboard',
    [ROLES.SELF_SERVICE_FBA]: '/self-service-fba/dashboard',
    [ROLES.WORKSTATION_PREPARATION]: '/workstation/preparation',
    [ROLES.WORKSTATION_RECEIVING]: '/workstation/receiving',
    [ROLES.WORKSTATION_RECEIVING_SECURE]: '/workstation/receiving-secure',
    [ROLES.WORKSTATION_RETURN]: '/workstation/return'
  };
  return routes[role] || '/dashboard';
};

export const getAvailableModules = (role) => {
  const modules = {
    [ROLES.ADMIN]: ['wms', 'tms', 'oms', 'admin', 'manager', 'workstations'],
    [ROLES.MANAGER]: ['wms', 'tms', 'oms', 'manager', 'workstations'],
    [ROLES.CLIENT]: ['wms', 'tms', 'oms'],
    [ROLES.OPERATOR]: ['wms', 'tms', 'workstations'],
    [ROLES.TEAM_LEADER]: ['wms', 'tms', 'oms', 'team-management', 'workstations'],
    [ROLES.ANALYST]: ['wms', 'tms', 'oms', 'analytics'],
    [ROLES.SELF_SERVICE_FBA]: ['oms', 'tms', 'wms'],
    [ROLES.WORKSTATION_PREPARATION]: ['workstation-preparation'],
    [ROLES.WORKSTATION_RECEIVING]: ['workstation-receiving'],
    [ROLES.WORKSTATION_RECEIVING_SECURE]: ['workstation-receiving-secure'],
    [ROLES.WORKSTATION_RETURN]: ['workstation-return']
  };
  return modules[role] || ['wms'];
};
